import { casOpsApi, CollectionModel, QueryTag, TagList } from '../api';
import { ModelWithId } from './commonModels';

export interface GeofenceSpeedLimitModel extends ModelWithId {
  speed: number; // 1 to 9999. No specific unit of measurement to allow for metric and imperial config.
  desc: string; // description (max 255 chars)
  used: boolean; // disable delete
}

export interface CreateGeofenceSpeedLimitModel
  extends Omit<GeofenceSpeedLimitModel, 'id' | 'used'> {}

export interface EditGeofenceSpeedLimitModel extends Omit<GeofenceSpeedLimitModel, 'id' | 'used'> {}

export interface GeofenceSpeedLimitFormModel
  extends CreateGeofenceSpeedLimitModel,
    EditGeofenceSpeedLimitModel {}

export const geofenceSpeedLimitsApi = casOpsApi.injectEndpoints({
  endpoints: build => ({
    listGeofenceSpeedLimits: build.query<GeofenceSpeedLimitModel[], void>({
      query: () => 'api/geofencesettings/speedlimits',
      transformResponse: (response: CollectionModel<GeofenceSpeedLimitModel>, meta, arg) =>
        response.items,
      providesTags: (result, error, arg) =>
        result ? [{ type: QueryTag.GeofenceSpeedLimit, id: TagList }] : [],
    }),
    getGeofenceSpeedLimit: build.query<GeofenceSpeedLimitModel, ModelWithId>({
      query: ({ id }) => `api/geofencesettings/speedlimits/${id}`,
      providesTags: (result, error, { id }) =>
        result ? [{ type: QueryTag.GeofenceSpeedLimit, id: id }] : [],
    }),
    createGeofenceSpeedLimit: build.mutation<void, CreateGeofenceSpeedLimitModel>({
      query: body => ({
        url: 'api/geofencesettings/speedlimits',
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: QueryTag.GeofenceSpeedLimit, id: TagList },
        QueryTag.GeofenceVersion,
      ],
    }),
    editGeofenceSpeedLimit: build.mutation<void, EditGeofenceSpeedLimitModel & ModelWithId>({
      query: ({ id, ...body }) => ({
        url: `api/geofencesettings/speedlimits/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (result, error, { id }) =>
        !error
          ? [
              { type: QueryTag.GeofenceSpeedLimit, id: TagList },
              { type: QueryTag.GeofenceSpeedLimit, id: id },
              QueryTag.GeofenceVersion,
            ]
          : [],
    }),
    deleteGeofenceSpeedLimit: build.mutation<void, ModelWithId>({
      query: ({ id }) => ({
        url: `api/geofencesettings/speedlimits/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { id }) =>
        !error
          ? [
              { type: QueryTag.GeofenceSpeedLimit, id: TagList },
              { type: QueryTag.GeofenceSpeedLimit, id: id },
              QueryTag.GeofenceVersion,
            ]
          : [],
    }),
  }),
  overrideExisting: false,
});

export const {
  useListGeofenceSpeedLimitsQuery,
  useGetGeofenceSpeedLimitQuery,
  useCreateGeofenceSpeedLimitMutation,
  useEditGeofenceSpeedLimitMutation,
  useDeleteGeofenceSpeedLimitMutation,
} = geofenceSpeedLimitsApi;
